import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recordsApi } from '../api/records';
import { adminApi } from '../api/admin';
import ScreenRenderer from '../components/screens/ScreenRenderer';
import toast from 'react-hot-toast';
import { documentsApi } from '../api/documents';
import LookupField from '../components/fields/LookupField';
import IntakeWizard from '../components/record/IntakeWizard';
import { ArrowLeft, Save, Plus, X, ChevronLeft, ChevronRight, Eye, EyeOff, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import UnsavedChangesModal from '../components/UnsavedChangesModal';
import { tagsApi } from '../api/tags';
import { useAuthStore } from '../store/authStore';
import { DEFAULT_CONFIG } from '../components/admin/WidgetConfigurator';
import NotesWidget from '../components/record/NotesWidget';
import ActivityTimeline from '../components/record/ActivityTimeline';
import TeamWidget from '../components/record/TeamWidget';
import MiniCalendar from '../components/record/MiniCalendar';
import VitalsWidget from '../components/record/VitalsWidget';
import RecordSummary from '../components/record/RecordSummary';
import TaskPreviewWidget from '../components/record/TaskPreviewWidget';
import DocumentPreviewWidget from '../components/record/DocumentPreviewWidget';
import TagsWidget from '../components/record/TagsWidget';
import LinkedRecordsWidget from '../components/record/LinkedRecordsWidget';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function RecordCreate() {
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const [recordType, setRecordType] = useState(null);
  const [fields, setFields] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [screenLayout, setScreenLayout] = useState(null);
  const [fieldData, setFieldData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const { tenant } = useAuthStore();
  const [wizardStep, setWizardStep] = useState(0); // 0 = wizard (if needed), 1 = main form
  const [wizardLinkField, setWizardLinkField] = useState(null);
  const [wizardLinkedId, setWizardLinkedId] = useState(null);
  const [wizardLinkedRecord, setWizardLinkedRecord] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Warn on navigation with unsaved changes (in-app + browser close/refresh)
  const unsavedBlocker = useUnsavedChanges(dirty);

  const widgetConfig = tenant?.settings?.widget_config || DEFAULT_CONFIG;

  // Widgets that make sense on create (no recordId yet)
  const createSafeWidgets = ['summary', 'team', 'calendar'];

  const renderWidget = (widgetId) => {
    // Most widgets need a recordId which doesn't exist yet on create
    // Show a placeholder for widgets that need it
    switch (widgetId) {
      case 'summary':
        return (
          <div key="summary" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Quick Summary</h3>
            <p className="text-xs text-gray-400">Summary will populate after saving.</p>
          </div>
        );
      case 'notes':
        return (
          <div key="notes" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
            <p className="text-xs text-gray-400">Add notes after creating the record.</p>
          </div>
        );
      case 'documents':
        return (
          <div key="documents" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Documents</h3>
            <p className="text-xs text-gray-400">Upload documents after creating the record.</p>
          </div>
        );
      case 'tasks':
        return (
          <div key="tasks" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Tasks</h3>
            <p className="text-xs text-gray-400">Tasks will be available after saving.</p>
          </div>
        );
      case 'linked':
        return (
          <div key="linked" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Linked Records</h3>
            <p className="text-xs text-gray-400">Link records after creating.</p>
          </div>
        );
      case 'activity':
        return (
          <div key="activity" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Activity</h3>
            <p className="text-xs text-gray-400">Activity will show after saving.</p>
          </div>
        );
      case 'vitals':
        return (
          <div key="vitals" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Vitals</h3>
            <p className="text-xs text-gray-400">Vitals will populate after saving.</p>
          </div>
        );
      case 'calendar':
        return (
          <div key="calendar" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Calendar</h3>
            <p className="text-xs text-gray-400">Calendar events available after saving.</p>
          </div>
        );
      case 'team':
        return (
          <div key="team" className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Team</h3>
            <p className="text-xs text-gray-400">Assign team after creating the record.</p>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const rtRes = await adminApi.getRecordTypes();
        const rt = rtRes.data.record_types.find(r => r.slug === typeSlug);
        if (!rt) { toast.error('Record type not found'); navigate('/'); return; }
        setRecordType(rt);

        const fieldsRes = await adminApi.getFields(rt.id);
        setFields(fieldsRes.data.fields);

        // Set defaults
        const defaults = {};
        fieldsRes.data.fields.forEach(f => {
          if (f.default_value !== null) defaults[f.id] = f.default_value;
          if (f.field_type === 'CHECKBOX' || f.field_type === 'TOGGLE') defaults[f.id] = false;
        });
        setFieldData(defaults);
        // Detect RECORD_LINK fields for intake wizard (only if field opts in via config)
        const contactLink = fieldsRes.data.fields.find(f =>
          f.field_type === 'RECORD_LINK' &&
          f.config?.show_intake_wizard === true
        );
        if (contactLink) {
          setWizardLinkField(contactLink);
          setWizardStep(0);
        } else {
          setWizardStep(1);
        }

        // Check for a default screen
        try {
          const screensRes = await adminApi.getScreens(rt.id);
          const defaultScreen = screensRes.data.screens.find(s => s.is_default && s.is_active);
          if (defaultScreen) {
            const screenRes = await adminApi.getScreen(defaultScreen.id);
            if (screenRes.data.screen.layout) {
              setScreenLayout(screenRes.data.screen.layout);
            }
          }
        } catch (e) {
          // No screens — use fallback
        }
      } catch (err) {
        toast.error('Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    tagsApi.list().then(data => setAllTags(data.tags || [])).catch(() => {});
  }, [typeSlug]);

  const handleFieldChange = (fieldId, value) => {
    setFieldData(prev => ({ ...prev, [fieldId]: value }));
    setDirty(true);
    if (errors[fieldId]) setErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  // Fetch workflows when record type is loaded
  useEffect(() => {
    if (!recordType?.id) { setWorkflows([]); return; }
    recordsApi.getWorkflowsForRecordType(recordType.id).then(res => {
      const wfs = res.data?.workflows || [];
      setWorkflows(wfs);
      const def = wfs.find(w => w.is_default);
      setSelectedWorkflowId(def ? def.id : wfs.length > 0 ? wfs[0].id : '');
    }).catch(() => setWorkflows([]));
  }, [recordType]);

  // Load screen layout for selected workflow first stage
  useEffect(function() {
    if (!selectedWorkflowId || !recordType) return;
    var loadWfScreen = async function() {
      try {
        var wfRes = await adminApi.getWorkflows(recordType.id);
        var wf = (wfRes.data.workflows || []).find(function(w) { return w.id === selectedWorkflowId; });
        if (!wf || !wf.stages || wf.stages.length === 0) return;
        var firstStage = wf.stages[0];
        // Fetch stage screens for the first stage
        var ssRes = await adminApi.getStageScreens(wf.id, firstStage.id);
        var stageScreens = ssRes.data.stage_screens || ssRes.data || [];
        if (stageScreens.length > 0) {
          var screenId = stageScreens[0].screen_id || stageScreens[0].id;
          if (screenId) {
            var screenRes = await adminApi.getScreen(screenId);
            if (screenRes.data.screen.layout) { setScreenLayout(screenRes.data.screen.layout); return; }
          }
        }
        // Fallback to legacy screen_id
        if (firstStage.screen_id) {
          var screenRes2 = await adminApi.getScreen(firstStage.screen_id);
          if (screenRes2.data.screen.layout) { setScreenLayout(screenRes2.data.screen.layout); return; }
        }
      } catch (e) { console.error('Failed to load workflow screen:', e); }
    };
    loadWfScreen();
  }, [selectedWorkflowId, recordType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      // Include wizard-linked contact in field data
      const submitData = { ...fieldData };
      if (wizardLinkField && wizardLinkedId) {
        submitData[wizardLinkField.id] = wizardLinkedId;
      }
      const res = await recordsApi.create({
        record_type_id: recordType.id,
        workflow_id: selectedWorkflowId || undefined,
        field_data: submitData,
      });
      const newRecordId = res.data.record.id;
      // Apply tags
      for (const tagId of selectedTags) {
        try { await tagsApi.addToRecord(newRecordId, tagId); } catch(e) {}
      }
      toast.success('Record created');
      setDirty(false);
      navigate("/records/" + typeSlug + "/" + newRecordId);
    } catch (err) {
      if (err.response?.data?.field_errors) {
        setErrors(err.response.data.field_errors);
        toast.error('Please fix the errors below');
      } else {
        toast.error(err.response?.data?.error || 'Create failed');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  // ═══ Wizard Step 0: Select/Create linked record ═══
  if (wizardLinkField && wizardStep === 0) {
    return (
      <div className="max-w-4xl mx-auto py-4">
        {/* Back button */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/records/" + typeSlug)} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">New {recordType?.name}</h1>
            <p className="text-sm text-gray-500">Start by selecting or creating the linked {wizardLinkField.config?.target_type_name?.toLowerCase() || 'record'}</p>
          </div>
        </div>

        <IntakeWizard
          linkField={wizardLinkField}
          recordTypeName={recordType?.name}
          onComplete={(linkedId, linkedRecord) => {
            setWizardLinkedId(linkedId);
            setWizardLinkedRecord(linkedRecord);
            setFieldData(prev => ({ ...prev, [wizardLinkField.id]: linkedId }));
            setWizardStep(1);
          }}
          onSkip={() => setWizardStep(1)}
        />
      </div>
    );
  }

  // Check if we have sidebar widgets configured
  const hasLeftWidgets = (widgetConfig.left || []).length > 0;
  const hasRightWidgets = (widgetConfig.right || []).length > 0;
  const hasSidebars = hasLeftWidgets || hasRightWidgets;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/records/" + typeSlug)} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">New {recordType?.name}</h1>
            <p className="text-sm text-gray-500">Fill in the details below</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {workflows.length > 1 && (
            <div className="flex items-center gap-2 mr-2">
              <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Workflow:</label>
              <select value={selectedWorkflowId} onChange={e => setSelectedWorkflowId(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500">
                {workflows.map(wf => (
                  <option key={wf.id} value={wf.id}>{wf.name}{wf.is_default ? ' (default)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          <button type="button" onClick={() => navigate("/records/" + typeSlug)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={saving || fields.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Create Record'}
          </button>
        </div>
      </div>

      {/* Wizard step indicator + linked record banner */}
      {wizardLinkedRecord && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-100 rounded-full text-xs font-semibold text-blue-700">
              <span className="h-4 w-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[9px]">2</span>
              Step 2 of 2
            </div>
            <span className="text-sm text-blue-900">
              {wizardLinkField?.label}: <strong>{wizardLinkedRecord.title || wizardLinkedRecord.reference_number || 'Linked'}</strong>
            </span>
          </div>
          <button onClick={() => { setWizardStep(0); setWizardLinkedId(null); setWizardLinkedRecord(null); }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium">Change</button>
        </div>
      )}
      {fields.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="text-4xl mb-3">&#9888;</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No fields defined</h3>
          <p className="text-sm text-gray-500 mb-4">This record type has no fields yet. An admin needs to add fields before records can be created.</p>
          <button type="button" onClick={function() { navigate('/admin/record-types'); }}
            className="text-sm text-brand-600 hover:text-brand-700 font-medium">Go to Admin &rarr;</button>
        </div>
      ) : (
      <form onSubmit={handleSubmit}>
        {/* 3-column layout with sidebars */}
        <div className="flex gap-4">
          {/* Left panel */}
          {hasSidebars && hasLeftWidgets && (
            <div className={`flex-shrink-0 transition-all duration-300 ${leftOpen ? 'w-[260px]' : 'w-10'}`}>
              {leftOpen ? (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button type="button" onClick={() => setLeftOpen(false)} title="Collapse left panel"
                      className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Tags widget on create */}
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tags</label>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {selectedTags.map(tagId => {
                        const tag = allTags.find(t => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                            {tag.name}
                            <button type="button" onClick={() => setSelectedTags(prev => prev.filter(id => id !== tag.id))}
                              className="hover:text-red-500"><X className="h-3 w-3" /></button>
                          </span>
                        );
                      })}
                      <div className="relative">
                        <button type="button" onClick={() => setShowTagPicker(!showTagPicker)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-400 border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-500">
                          <Plus className="h-3 w-3" /> Tag
                        </button>
                        {showTagPicker && (
                          <div className="absolute z-50 mt-1 left-0 w-52 bg-white rounded-lg border border-gray-200 shadow-xl">
                            <div className="max-h-[200px] overflow-y-auto">
                              {allTags.filter(t => !selectedTags.includes(t.id)).map(tag => (
                                <button key={tag.id} type="button" onClick={() => { setSelectedTags(prev => [...prev, tag.id]); setShowTagPicker(false); }}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-xs">
                                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                                  {tag.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {(widgetConfig.left || []).map(id => renderWidget(id))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 pt-1">
                  <button type="button" onClick={() => setLeftOpen(true)} title="Expand left panel"
                    className="p-2 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 text-gray-500 transition-all">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Center: Form */}
          <div className="flex-1 min-w-0">
            {/* Tags inline if no sidebars */}
            {!hasSidebars && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {selectedTags.map(tagId => {
                    const tag = allTags.find(t => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                        {tag.name}
                        <button type="button" onClick={() => setSelectedTags(prev => prev.filter(id => id !== tag.id))}
                          className="hover:text-red-500"><X className="h-3 w-3" /></button>
                      </span>
                    );
                  })}
                  <div className="relative">
                    <button type="button" onClick={() => setShowTagPicker(!showTagPicker)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-400 border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-500">
                      <Plus className="h-3 w-3" /> Tag
                    </button>
                    {showTagPicker && (
                      <div className="absolute z-50 mt-1 left-0 w-52 bg-white rounded-lg border border-gray-200 shadow-xl">
                        <div className="max-h-[200px] overflow-y-auto">
                          {allTags.filter(t => !selectedTags.includes(t.id)).map(tag => (
                            <button key={tag.id} type="button" onClick={() => { setSelectedTags(prev => [...prev, tag.id]); setShowTagPicker(false); }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-xs">
                              <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Use ScreenRenderer if layout exists, else fallback */}
            {screenLayout ? (
              <ScreenRenderer
                layout={screenLayout}
                fields={fields}
                fieldData={fieldData}
                onChange={handleFieldChange}
                errors={errors}
                readOnly={false}
              />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                <div className="p-6 space-y-5">
                  {fields.map((field) => (
                    <FieldInput
                      key={field.id}
                      field={field}
                      value={fieldData[field.id]}
                      onChange={(val) => handleFieldChange(field.id, val)}
                      error={errors[field.id]}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bottom save bar */}
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => navigate("/records/" + typeSlug)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Create Record'}
              </button>
            </div>
          </div>

          {/* Right panel */}
          {hasSidebars && hasRightWidgets && (
            <div className={`flex-shrink-0 transition-all duration-300 ${rightOpen ? 'w-[260px]' : 'w-10'}`}>
              {rightOpen ? (
                <div className="space-y-4">
                  <div className="flex justify-start">
                    <button type="button" onClick={() => setRightOpen(false)} title="Collapse right panel"
                      className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  {(widgetConfig.right || []).map(id => renderWidget(id))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 pt-1">
                  <button type="button" onClick={() => setRightOpen(true)} title="Expand"
                    className="p-2 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 text-gray-500 transition-all">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </form>
      )}
      <UnsavedChangesModal blocker={unsavedBlocker} />
    </div>
  );
}

// Dynamic field input renderer

function UserLookupField({ field, value, onChange, readOnly }) {
  const [users, setUsers] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef(null);
  const apiClient = require('../api/client').default;

  React.useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const params = {};
        if (field.config?.role_filter) {
          params.role_id = field.config.role_filter;
        }
        const res = await apiClient.get('/api/users', { params });
        setUsers(res.data.users || res.data || []);
      } catch (err) {
        console.error('Failed to load users:', err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [field.config?.role_filter]);

  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const filtered = users.filter(u =>
    !search || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const selectedUser = users.find(u => u.id === value);

  if (value && selectedUser) {
    return (
      <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold">
            {(selectedUser.name || selectedUser.email || '?')[0].toUpperCase()}
          </div>
          <span className="text-sm text-gray-800">{selectedUser.name || selectedUser.email}</span>
          {selectedUser.role_name && (
            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: (selectedUser.role_color || '#6366F1') + '15', color: selectedUser.role_color || '#6366F1' }}>
              {selectedUser.role_name}
            </span>
          )}
        </div>
        {!readOnly && <button onClick={() => onChange(null)} className="text-gray-400 hover:text-red-500 text-xs">Clear</button>}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-600 cursor-not-allowed">
        {loading ? 'Loading users...' : 'No user selected'}
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <input type="text" value={search} onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={loading ? 'Loading users...' : 'Search team members...'}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 max-h-48 overflow-y-auto">
          {filtered.map(u => (
            <button key={u.id}
              onClick={() => { onChange(u.id); setSearch(''); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left">
              <div className="h-6 w-6 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold">
                {(u.name || u.email || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-gray-800">{u.name || 'Unnamed'}</span>
                {u.email && <span className="text-gray-400 text-xs ml-2">{u.email}</span>}
              </div>
              {u.role_name && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0" style={{ backgroundColor: (u.role_color || '#6366F1') + '15', color: u.role_color || '#6366F1' }}>
                  {u.role_name}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && !loading && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 px-3 py-2 text-xs text-gray-400">
          No users found
        </div>
      )}
    </div>
  );
}


function formatPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
  return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
}

function RepeaterField({ field, value, onChange, readOnly }) {
  const columns = field.config?.columns || [];
  const rows = value || [];
  const minRows = field.config?.min_rows || 0;
  const maxRows = field.config?.max_rows || 100;

  const addRow = () => {
    if (rows.length >= maxRows) return;
    const emptyRow = {};
    columns.forEach(col => { emptyRow[col.name] = ''; });
    onChange([...rows, emptyRow]);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const updateCell = (rowIndex, colName, val) => {
    const updated = [...rows];
    updated[rowIndex] = { ...updated[rowIndex], [colName]: val };
    onChange(updated);
  };

  const renderCell = (col, rowVal, rowIndex) => {
    const cellVal = rowVal || '';
    const cellClass = `w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500${readOnly ? ' bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`;
    switch (col.type) {
      case 'number':
        return (
          <div className="relative">
            <input type="number" step="1"
              value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value ? Number(e.target.value) : '')}
              readOnly={readOnly} className={cellClass} />
          </div>
        );
      case 'currency':
        return (
          <div className="relative">
            <span className="absolute left-2 top-1.5 text-xs text-gray-400">{col.currency_symbol || '$'}</span>
            <input type="number" step="0.01"
              value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value ? Number(e.target.value) : '')}
              onKeyDown={e => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }}
              onPaste={e => { if (/[eE+\-]/.test(e.clipboardData.getData('text'))) e.preventDefault(); }}
              onBlur={e => {
                if (e.target.value !== '') {
                  const rounded = Math.round(Number(e.target.value) * 100) / 100;
                  updateCell(rowIndex, col.name, isNaN(rounded) ? '' : rounded);
                }
              }}
              readOnly={readOnly} className={`${cellClass} pl-5`} />
          </div>
        );
      case 'date':
        return <input type="date" value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value)}
          onClick={e => { if (!readOnly) e.target.showPicker?.(); }} readOnly={readOnly} className={cellClass} />;
      case 'year':
        return <input type="number" min="1900" max="2100" placeholder="YYYY"
          value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value)} readOnly={readOnly} className={`${cellClass} w-24`} />;
      case 'phone':
        return <input type="tel" value={formatPhone(cellVal)}
          onChange={e => updateCell(rowIndex, col.name, e.target.value.replace(/\D/g, '').slice(0, 10))}
          readOnly={readOnly} placeholder="(555) 555-5555" className={cellClass} />;
      case 'email':
        return <input type="email" value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value)} readOnly={readOnly} className={cellClass} />;
      case 'dropdown':
        return (
          <select value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value)} disabled={readOnly} className={cellClass}>
            <option value="">Select...</option>
            {(col.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      default:
        return <input type="text" value={cellVal} onChange={e => updateCell(rowIndex, col.name, e.target.value)} readOnly={readOnly} className={cellClass} />;
    }
  };

  if (columns.length === 0) {
    return <div className="text-sm text-gray-400 italic border border-dashed border-gray-200 rounded-lg p-3">No columns configured for this repeater field.</div>;
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="grid gap-2 items-center" style={{ gridTemplateColumns: readOnly ? `repeat(${columns.length}, 1fr)` : `repeat(${columns.length}, 1fr) 36px` }}>
        {columns.map(col => (
          <span key={col.name} className="text-xs font-medium text-gray-500 truncate">
            {col.label}{col.required ? ' *' : ''}
          </span>
        ))}
        {!readOnly && <span></span>}
      </div>

      {/* Rows */}
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="grid gap-2 items-start" style={{ gridTemplateColumns: readOnly ? `repeat(${columns.length}, 1fr)` : `repeat(${columns.length}, 1fr) 36px` }}>
          {columns.map(col => (
            <div key={col.name}>
              {renderCell(col, row[col.name], rowIndex)}
            </div>
          ))}
          {!readOnly && (
            <button onClick={() => removeRow(rowIndex)}
              className="mt-1 p-1 text-gray-300 hover:text-red-500 transition-colors" title="Remove row">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
          )}
        </div>
      ))}

      {/* Add row button */}
      {!readOnly && rows.length < maxRows && (
        <button onClick={addRow}
          className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-xs text-gray-400 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50 transition-all">
          + Add Row {minRows > 0 && rows.length < minRows ? `(${minRows - rows.length} more required)` : ''}
        </button>
      )}
      {rows.length >= maxRows && (
        <p className="text-xs text-amber-600">Maximum of {maxRows} rows reached</p>
      )}
    </div>
  );
}


function RepeaterSectionField({ field, value, onChange, readOnly }) {
  const [expanded, setExpanded] = useState(true);
  const subFields = field.config?.sub_fields || [];
  const rows = Array.isArray(value) ? value : [];
  const maxRows = Math.min(field.config?.max_rows || 10, 10);
  const sectionLabel = field.config?.section_label || field.label;

  const addRow = () => {
    if (rows.length >= maxRows) return;
    const emptyRow = {};
    subFields.forEach(sf => { emptyRow[sf.key] = ''; });
    onChange([...rows, emptyRow]);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const updateCell = (rowIndex, key, val) => {
    const updated = [...rows];
    updated[rowIndex] = { ...updated[rowIndex], [key]: val };
    onChange(updated);
  };

  const renderSubFieldInput = (sf, cellVal, rowIndex) => {
    const cellClass = 'w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500';
    const roClass = readOnly ? ' bg-gray-50 text-gray-600 cursor-not-allowed' : '';
    const effectiveUpdate = readOnly ? () => {} : (val) => updateCell(rowIndex, sf.key, val);

    switch (sf.type) {
      case 'TEXT':
      case 'URL':
        return <input type="text" value={cellVal || ''} onChange={e => effectiveUpdate(e.target.value)}
          readOnly={readOnly} className={cellClass + roClass} />;
      case 'TEXTAREA':
        return <textarea value={cellVal || ''} onChange={e => effectiveUpdate(e.target.value)}
          readOnly={readOnly} rows={2} className={cellClass + roClass} />;
      case 'NUMBER':
        return <input type="number" step="any" value={cellVal ?? ''} readOnly={readOnly}
          onChange={e => effectiveUpdate(e.target.value ? Number(e.target.value) : null)}
          className={cellClass + roClass} />;
      case 'CURRENCY': {
        const sym = sf.currency_symbol || '$';
        return (
          <div className="relative">
            <span className="absolute left-2.5 top-2 text-xs text-gray-400">{sym}</span>
            <input type="number" step="0.01" value={cellVal ?? ''} readOnly={readOnly}
              onChange={e => effectiveUpdate(e.target.value ? Number(e.target.value) : null)}
              onKeyDown={e => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }}
              onPaste={e => { if (/[eE+\-]/.test(e.clipboardData.getData('text'))) e.preventDefault(); }}
              onBlur={e => {
                if (!readOnly && e.target.value !== '') {
                  const rounded = Math.round(Number(e.target.value) * 100) / 100;
                  effectiveUpdate(isNaN(rounded) ? null : rounded);
                }
              }}
              className={`${cellClass} pl-6${roClass}`} placeholder="0.00" />
          </div>
        );
      }
      case 'DATE':
        return <input type="date" value={cellVal || ''} readOnly={readOnly}
          onClick={e => !readOnly && e.target.showPicker?.()}
          onChange={e => effectiveUpdate(e.target.value)} className={cellClass + roClass} />;
      case 'DATETIME':
        return <input type="datetime-local" value={cellVal || ''} readOnly={readOnly}
          onClick={e => !readOnly && e.target.showPicker?.()}
          onChange={e => effectiveUpdate(e.target.value)} className={cellClass + roClass} />;
      case 'DROPDOWN':
        return (
          <select value={cellVal || ''} onChange={e => effectiveUpdate(e.target.value)}
            disabled={readOnly} className={cellClass + roClass}>
            <option value="">Select...</option>
            {(sf.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'MULTI_SELECT': {
        const selected = Array.isArray(cellVal) ? cellVal : [];
        return (
          <div className="flex flex-wrap gap-1">
            {(sf.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-50 rounded border border-gray-200 cursor-pointer">
                <input type="checkbox" checked={selected.includes(opt)} disabled={readOnly}
                  onChange={e => {
                    const next = e.target.checked ? [...selected, opt] : selected.filter(v => v !== opt);
                    effectiveUpdate(next);
                  }}
                  className="h-3 w-3 rounded border-gray-300 text-brand-600" />
                {opt}
              </label>
            ))}
          </div>
        );
      }
      case 'CHECKBOX':
      case 'TOGGLE':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!cellVal} disabled={readOnly}
              onChange={e => effectiveUpdate(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600" />
            <span className="text-sm text-gray-700">{cellVal ? 'Yes' : 'No'}</span>
          </label>
        );
      case 'PHONE':
        return <input type="tel" value={formatPhone(cellVal)} readOnly={readOnly}
          onChange={e => effectiveUpdate(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="(555) 555-5555"
          className={cellClass + roClass} />;
      case 'EMAIL':
        return <input type="email" value={cellVal || ''} readOnly={readOnly}
          onChange={e => effectiveUpdate(e.target.value)} className={cellClass + roClass} />;
      case 'RECORD_LINK':
        return <input type="text" value={cellVal || ''} readOnly={readOnly}
          onChange={e => effectiveUpdate(e.target.value)} placeholder="Record ID"
          className={cellClass + roClass} />;
      default:
        return <input type="text" value={cellVal || ''} readOnly={readOnly}
          onChange={e => effectiveUpdate(e.target.value)} className={cellClass + roClass} />;
    }
  };

  const formatReadOnlyValue = (sf, val) => {
    if (val === null || val === undefined || val === '') return <span className="text-gray-300">—</span>;
    switch (sf.type) {
      case 'CURRENCY':
        return <span>{sf.currency_symbol || '$'}{Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
      case 'CHECKBOX':
      case 'TOGGLE':
        return <span>{val ? 'Yes' : 'No'}</span>;
      case 'MULTI_SELECT':
        return <span>{Array.isArray(val) ? val.join(', ') : val}</span>;
      default:
        return <span>{String(val)}</span>;
    }
  };

  if (subFields.length === 0) {
    return <div className="text-sm text-gray-400 italic border border-dashed border-gray-200 rounded-lg p-3">No sub-fields configured for this repeater section.</div>;
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
        <span className="text-sm font-semibold text-gray-700">{sectionLabel} <span className="text-xs font-normal text-gray-400">({rows.length} {rows.length === 1 ? 'entry' : 'entries'})</span></span>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="p-4 space-y-3">
          {rows.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No entries yet. {!readOnly && "Click 'Add Row' to begin."}</p>
          )}

          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="bg-gray-50 rounded-lg p-3 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-400">#{rowIndex + 1}</span>
                {!readOnly && (
                  <button onClick={() => removeRow(rowIndex)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors" title="Remove row">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subFields.map(sf => (
                  <div key={sf.key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {sf.label}{sf.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {readOnly
                      ? <div className="text-sm text-gray-900">{formatReadOnlyValue(sf, row[sf.key])}</div>
                      : renderSubFieldInput(sf, row[sf.key], rowIndex)
                    }
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!readOnly && rows.length < maxRows && (
            <button onClick={addRow} type="button"
              className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-xs text-gray-400 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50 transition-all flex items-center justify-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add Row
            </button>
          )}
          {!readOnly && rows.length >= maxRows && (
            <p className="text-xs text-amber-600 text-center">Maximum of {maxRows} entries reached</p>
          )}
        </div>
      )}
    </div>
  );
}


function CalculatedFieldDisplay({ field, fieldData, fields, baseClass }) {
  const config = field.config || {};
  const calcType = config.calculation_type || 'math';

  const computeValue = () => {
    try {
      if (calcType === 'date_offset') {
        const sourceFieldId = config.source_field;
        if (!sourceFieldId || !fieldData) return null;
        const sourceVal = fieldData[sourceFieldId];
        if (!sourceVal) return null;
        const sourceDate = new Date(sourceVal);
        if (isNaN(sourceDate.getTime())) return null;
        const amount = parseInt(config.offset_amount) || 0;
        const unit = config.offset_unit || 'years';
        const result = new Date(sourceDate);
        if (unit === 'days') result.setDate(result.getDate() + amount);
        else if (unit === 'months') result.setMonth(result.getMonth() + amount);
        else if (unit === 'years') result.setFullYear(result.getFullYear() + amount);
        return result;
      }

      if (calcType === 'date_diff') {
        if (!fieldData) return null;
        const startFieldId = config.start_date_field;
        const endFieldId = config.end_date_field;
        if (!startFieldId) return null;
        const startVal = fieldData[startFieldId];
        if (!startVal) return null;
        const startDate = new Date(startVal);
        if (isNaN(startDate.getTime())) return null;
        const endDate = endFieldId === '_today' || !endFieldId ? new Date() : new Date(fieldData[endFieldId] || '');
        if (isNaN(endDate.getTime())) return null;
        const diffMs = endDate.getTime() - startDate.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffUnit = config.diff_unit || 'days';
        if (diffUnit === 'days') return diffDays;
        if (diffUnit === 'months') return Math.floor(diffDays / 30.44);
        if (diffUnit === 'years') return (diffDays / 365.25).toFixed(1);
        return diffDays;
      }

      // Math formula
      if (!config.formula || !fieldData || !fields) return null;
      let expr = config.formula;
      (fields || []).forEach(f => {
        const regex = new RegExp('\{' + (f.name || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\}', 'g');
        const val = fieldData[f.id];
        expr = expr.replace(regex, val != null && val !== '' ? String(Number(val) || 0) : '0');
      });
      expr = expr.replace(/[^0-9+\-*/().\s]/g, '');
      if (!expr.trim()) return null;
      return Function('"use strict"; return (' + expr + ')')();
    } catch (e) {
      return null;
    }
  };

  const computed = computeValue();

  const formatValue = (val) => {
    if (val === null || val === undefined) return null;
    if (calcType === 'date_offset' && val instanceof Date) {
      return val.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    if (calcType === 'date_diff') {
      const unit = config.diff_unit || 'days';
      return `${val} ${unit}`;
    }
    const fmt = config.output_format || 'number';
    if (fmt === 'currency') return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 });
    if (fmt === 'percentage') return val + '%';
    return String(val);
  };

  const formatted = formatValue(computed);

  // Color coding for date_offset — warn if approaching
  let colorClass = 'text-gray-600';
  if (calcType === 'date_offset' && computed instanceof Date) {
    const daysUntil = Math.floor((computed.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) colorClass = 'text-red-600 font-semibold';
    else if (daysUntil < 90) colorClass = 'text-amber-600 font-medium';
    else if (daysUntil < 180) colorClass = 'text-yellow-600';
    else colorClass = 'text-green-600';
  }

  return (
    <div className={`${baseClass} bg-gray-50 cursor-not-allowed ${colorClass}`}>
      {formatted != null ? (
        <span>{formatted}</span>
      ) : (
        <span className="text-gray-400 italic">Auto-calculated — waiting for input</span>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange: onChangeProp, error, recordId, fieldData, fields, readOnly }) {
  const [phiRevealed, setPhiRevealed] = useState(false);
  const [phiEditing, setPhiEditing] = useState(false);

  const baseClass = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
    error ? 'border-red-300 bg-red-50' : 'border-gray-300'
  } ${readOnly ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`;
  const onChange = readOnly ? () => {} : onChangeProp;

  // PHI masking for fields marked as is_phi
  if (field.config?.is_phi === true && value && !phiEditing) {
    if (!phiRevealed) {
      const maskedValue = (() => {
        if (field.field_type === 'DATE' || field.field_type === 'DATETIME') {
          return '●●/●●/●●●●';
        }
        // TEXT-like fields: show last 4 if long enough
        const str = String(value);
        if (str.length >= 4) {
          return '●●●-●●-' + str.slice(-4);
        }
        return '●●●●●●●●●';
      })();

      const handleReveal = async () => {
        if (recordId) {
          try {
            const { recordsApi } = await import('../api/records');
            await recordsApi.logPhiReveal(recordId, field.id, field.name);
          } catch (e) {
            // Audit log failure should not block reveal
          }
        }
        setPhiRevealed(true);
      };

      return (
        <div>
          {field.label && <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}{field.is_required && <span className="text-red-500 ml-0.5">*</span>}</label>}
          <div className="relative">
            <div className={`w-full px-3 py-2 border rounded-lg text-sm bg-gray-100 border-gray-300 font-mono select-none ${readOnly ? 'cursor-not-allowed' : 'cursor-default'}`}>
              {maskedValue}
            </div>
            {!readOnly && (
              <button type="button" onClick={handleReveal}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-600 p-1"
                title="Reveal value">
                <Eye className="h-4 w-4" />
              </button>
            )}
          </div>
          {!readOnly && (
            <button type="button" onClick={() => setPhiEditing(true)}
              className="text-xs text-gray-400 hover:text-brand-600 mt-1">
              Edit without revealing
            </button>
          )}
          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
      );
    }

    // PHI revealed — show normal input with re-mask button (handled below in renderInput)
  }

  // PHI editing mode — empty input to overwrite without seeing old value
  if (field.config?.is_phi === true && phiEditing) {
    return (
      <div>
        {field.label && <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}{field.is_required && <span className="text-red-500 ml-0.5">*</span>}</label>}
        <div className="relative">
          <input
            type={field.field_type === 'DATE' ? 'date' : 'text'}
            className={baseClass}
            placeholder={`Enter new ${field.label || field.name}`}
            onChange={e => onChange(e.target.value)}
            onBlur={() => { if (!value) setPhiEditing(false); }}
            autoFocus
          />
          <button type="button" onClick={() => setPhiEditing(false)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-600 p-1"
            title="Cancel edit">
            <X className="h-4 w-4" />
          </button>
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  const renderInput = () => {
    switch (field.field_type) {
      case 'TEXT':
        return <input type='text'
          value={value || ''} onChange={e => onChange(e.target.value)} className={baseClass}
          readOnly={readOnly} placeholder={field.config?.placeholder || ''} />;
      case 'URL':
        if (readOnly && value) {
          return <a href={value.startsWith('http') ? value : 'https://' + value} target="_blank" rel="noopener noreferrer"
            className="text-sm text-brand-600 hover:text-brand-700 underline break-all">{value}</a>;
        }
        return <input type='url'
          value={value || ''} onChange={e => onChange(e.target.value)} className={baseClass}
          readOnly={readOnly} placeholder={field.config?.placeholder || 'https://...'} />;
      case 'PHONE':
        if (readOnly && value) {
          const digits = (value || '').replace(/\D/g, '');
          return <a href={'tel:' + digits} className="text-sm text-brand-600 hover:text-brand-700 underline">{formatPhone(value)}</a>;
        }
        return <input type='tel'
          value={formatPhone(value)} onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))} className={baseClass}
          readOnly={readOnly} placeholder={field.config?.placeholder || '(555) 555-5555'} />;
      case 'EMAIL':
        return <input type='email'
          value={value || ''} onChange={e => onChange(e.target.value)} className={baseClass}
          readOnly={readOnly} placeholder={field.config?.placeholder || ''} />;

      case 'TEXTAREA':
        return <textarea value={value || ''} onChange={e => onChange(e.target.value)}
          rows={3} className={baseClass} readOnly={readOnly} placeholder={field.config?.placeholder || ''} />;
      case 'RICH_TEXT': {
        const quillModules = { toolbar: [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'color': [] }, { 'background': [] }],
          ['link'],
          ['clean']
        ]};
        return (
          <div className="rich-text-wrapper">
            <ReactQuill theme="snow" value={value || ''} onChange={val => onChange(val)}
              modules={quillModules} readOnly={readOnly} placeholder={field.config?.placeholder || 'Type here...'} />
            <style>{'.rich-text-wrapper .ql-container { min-height: 120px; font-size: 14px; } .rich-text-wrapper .ql-editor { min-height: 120px; }'}</style>
          </div>
        );
      }

      case 'NUMBER':
        return <input type="number" step="any" value={value ?? ''}
          onChange={e => { const v = e.target.valueAsNumber; onChange(isNaN(v) ? null : v); }}
          onKeyDown={e => { if (['e','E','+'].includes(e.key)) e.preventDefault(); }}
          className={baseClass} readOnly={readOnly} min={field.config?.min} max={field.config?.max} />;

      case 'CURRENCY':
        const currSymbol = field.config?.currency_symbol || '$';
        const currDecimals = field.config?.decimals ?? 2;
        const currStep = currDecimals === 0 ? '1' : currDecimals === 4 ? '0.0001' : '0.01';
        return (
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-sm text-gray-400">{currSymbol}</span>
            <input type="number" step={currStep} value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
              onKeyDown={e => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }}
              onPaste={e => { if (/[eE+\-]/.test(e.clipboardData.getData('text'))) e.preventDefault(); }}
              onBlur={e => {
                if (e.target.value !== '') {
                  const rounded = Math.round(Number(e.target.value) * 100) / 100;
                  onChange(isNaN(rounded) ? null : rounded);
                }
              }}
              readOnly={readOnly} className={`${baseClass} pl-7`} placeholder="0.00" />
          </div>
        );

      case 'DATE':
        return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)}
          onClick={e => { if (!readOnly) e.target.showPicker?.(); }} readOnly={readOnly} className={baseClass} />;

      case 'DATETIME':
        return <input type="datetime-local" value={value || ''} onChange={e => onChange(e.target.value)}
          onClick={e => { if (!readOnly) e.target.showPicker?.(); }} readOnly={readOnly} className={baseClass} />;

      case 'DROPDOWN':
        return (
          <select value={value || ''} onChange={e => onChange(e.target.value)} disabled={readOnly} className={baseClass}>
            <option value="">Select...</option>
            {(field.config?.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'MULTI_SELECT':
        return (
          <div className="space-y-1">
            {(field.config?.options || []).map(opt => (
              <label key={opt} className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input type="checkbox" checked={(value || []).includes(opt)}
                  onChange={e => {
                    const current = value || [];
                    onChange(e.target.checked ? [...current, opt] : current.filter(v => v !== opt));
                  }}
                  disabled={readOnly}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600" />
                <span className="text-sm text-gray-700">{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'CHECKBOX':
        return (
          <div className="flex items-center gap-4">
            <label className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input type="radio" name={`chk-${field.id}`} checked={value === true}
                onChange={() => onChange(true)} disabled={readOnly}
                className="h-4 w-4 border-gray-300 text-brand-600" />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input type="radio" name={`chk-${field.id}`} checked={value === false}
                onChange={() => onChange(false)} disabled={readOnly}
                className="h-4 w-4 border-gray-300 text-brand-600" />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
        );
      case 'TOGGLE':
        return (
          <label className={`flex items-center gap-3 ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <button type="button" role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)} disabled={readOnly}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out ${value ? 'bg-brand-600' : 'bg-gray-300'} ${readOnly ? 'opacity-60' : ''}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${value ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-gray-700">{value ? 'On' : 'Off'}</span>
          </label>
        );

      case 'ADDRESS':
        const addr = value || {};
        const updateAddr = (key, val) => onChange({ ...addr, [key]: val });
        return (
          <div className="space-y-2">
            <input type="text" value={addr.street || ''} onChange={e => updateAddr('street', e.target.value)}
              readOnly={readOnly} placeholder="Street address" className={baseClass} />
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={addr.city || ''} onChange={e => updateAddr('city', e.target.value)}
                readOnly={readOnly} placeholder="City" className={baseClass} />
              <input type="text" value={addr.state || ''} onChange={e => updateAddr('state', e.target.value)}
                readOnly={readOnly} placeholder="State" className={baseClass} />
              <input type="text" value={addr.zip || ''} onChange={e => updateAddr('zip', e.target.value)}
                readOnly={readOnly} placeholder="ZIP" className={baseClass} />
            </div>
          </div>
        );

      case 'RECORD_LINK':
        return <LookupField field={field} value={value} onChange={onChange} recordId={recordId} readOnly={readOnly} />;

      case 'FILE':
        return <FileFieldUpload recordId={recordId} value={value} onChange={onChange} field={field} readOnly={readOnly} />;

      case 'USER_LOOKUP':
        return <UserLookupField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

      case 'CALCULATED':
        return <CalculatedFieldDisplay field={field} fieldData={fieldData} fields={fields} baseClass={baseClass} />;

      case 'REPEATER':
        return <RepeaterField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

      case 'REPEATER_SECTION':
        return <RepeaterSectionField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

      default:
        return <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} readOnly={readOnly} className={baseClass} />;
    }
  };

  // Wrap revealed PHI fields with re-mask button
  if (field.config?.is_phi === true && phiRevealed) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          {field.is_required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="relative">
          {renderInput()}
          <button type="button" onClick={() => setPhiRevealed(false)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-600 p-1 z-10"
            title="Re-mask value">
            <EyeOff className="h-4 w-4" />
          </button>
        </div>
        {field.help_text && <p className="text-xs text-gray-400 mt-1">{field.help_text}</p>}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        {field.is_required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderInput()}
      {field.help_text && <p className="text-xs text-gray-400 mt-1">{field.help_text}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}


function FileFieldUpload({ recordId, value, onChange, field, readOnly }) {
  const [uploading, setUploading] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [loadingFiles, setLoadingFiles] = React.useState(false);
  const fileInputRef = React.useRef();

  // Load existing files linked to this field
  React.useEffect(() => {
    if (!recordId || !value) return;
    // value stores array of doc IDs
    const ids = Array.isArray(value) ? value : [value];
    if (ids.length === 0) return;
    setLoadingFiles(true);
    documentsApi.getForRecord(recordId).then(data => {
      const linked = (data.documents || []).filter(d => ids.includes(d.id));
      setFiles(linked);
    }).catch(() => {}).finally(() => setLoadingFiles(false));
  }, [recordId, value]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (!recordId) {
      toast.error('Save the record first before uploading files');
      return;
    }
    setUploading(true);
    try {
      const category = field.config?.category || 'uncategorized';
      const uploaded = [];
      for (let i = 0; i < fileList.length; i++) {
        const res = await documentsApi.upload(fileList[i], recordId, category);
        uploaded.push(res.document || res);
      }
      const newIds = uploaded.map(d => d.id);
      const prev = Array.isArray(value) ? value : value ? [value] : [];
      onChange([...prev, ...newIds]);
      setFiles(f => [...f, ...uploaded]);
      toast.success(uploaded.length === 1 ? 'File uploaded' : uploaded.length + ' files uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleRemove = (docId) => {
    const prev = Array.isArray(value) ? value : value ? [value] : [];
    onChange(prev.filter(id => id !== docId));
    setFiles(f => f.filter(d => d.id !== docId));
  };

  const handlePreview = async (doc) => {
    try {
      const data = await documentsApi.getPreviewUrl(doc.id);
      window.open(data.previewable ? data.url : (await documentsApi.getDownloadUrl(doc.id)).url, '_blank');
    } catch { toast.error('Preview failed'); }
  };

  if (!recordId) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-sm text-gray-400">
        Save the record first to enable file uploads
      </div>
    );
  }

  return (
    <div>
      {!readOnly && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
          className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-brand-400 hover:bg-brand-50 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" multiple className="hidden"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }} />
          {uploading ? (
            <p className="text-xs text-gray-500">Uploading...</p>
          ) : (
            <p className="text-xs text-gray-500">Drop files here or <span className="text-brand-600 font-medium">browse</span></p>
          )}
        </div>
      )}
      {readOnly && files.length === 0 && (
        <div className="border border-gray-200 rounded-lg p-3 text-center bg-gray-50">
          <p className="text-xs text-gray-400">No files attached</p>
        </div>
      )}
      {loadingFiles && <p className="text-xs text-gray-400 mt-1">Loading files...</p>}
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map(doc => (
            <div key={doc.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2 group">
              <span className="flex-shrink-0">{doc.mime_type?.startsWith('image/') ? '\u{1F5BC}' : '\u{1F4C4}'}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); handlePreview(doc); }}
                className="flex-1 text-left truncate text-gray-700 hover:text-brand-600">
                {doc.original_name || doc.filename}
              </button>
              {!readOnly && (
                <button type="button" onClick={(e) => { e.stopPropagation(); handleRemove(doc.id); }}
                  className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove">&times;</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { FieldInput };
